/**
 * @mergeTarget
 * @module Src/Profiles
 */
/**
 * Enumetor for all profiles availables in Lisio
 * Please names new profiles as following :
 * ```
 * MY_NAME : "my_name"
 * ```
 * @source
 */
var LisioProfileNames;
(function (LisioProfileNames) {
    LisioProfileNames["DYSLEXIA"] = "dyslexia";
    LisioProfileNames["GLASS_X2"] = "glass_x2";
    LisioProfileNames["GLASS_X4"] = "glass_x4";
    LisioProfileNames["EPILEPSY"] = "epilepsy";
    LisioProfileNames["MOVEMENTS"] = "movements";
    LisioProfileNames["GESTURES"] = "gestures";
    LisioProfileNames["FOCUS"] = "focus";
    LisioProfileNames["READING"] = "reading";
    LisioProfileNames["LOW_EYE_SIGHT"] = "low_eye_sight";
    LisioProfileNames["VERY_LOW_EYE_SIGHT"] = "very_low_eye_sight";
    LisioProfileNames["VISION"] = "vision";
    LisioProfileNames["PRESBYOPIA"] = "presbyopia";
    LisioProfileNames["CATARACT"] = "cataract";
    LisioProfileNames["UNDERLINE"] = "underline";
    LisioProfileNames["INEXPERIENCE"] = "inexperience";
    LisioProfileNames["ATTENTION"] = "attention";
    LisioProfileNames["HEADACHE"] = "headache";
    LisioProfileNames["MEMORY"] = "memory";
    LisioProfileNames["LEARNING"] = "learning";
    LisioProfileNames["LOW_NETWORK"] = "low_network";
    LisioProfileNames["DICTIONARY"] = "dictionary";
    LisioProfileNames["ECOLO"] = "ecolo";
    LisioProfileNames["LIGHT_ECOLO"] = "light_ecolo";
    LisioProfileNames["DARK_MODE"] = "dark_mode";
})(LisioProfileNames || (LisioProfileNames = {}));
/**
 * @remarks
 * ## How to create a new profile ? Part 1
 *
 * To create a new profile is very easy, choose a revelant name and add it to {@link LisioProfileNames | LisioProfileNames}.\
 * Next go to {@link Src/Profiles.LisioProfileFactory | LisioProfileFactory}
 *
 * ## How to modify a profile ? Part 1
 *
 * If you don't want to rename your profile skip to part 2 {@link Src/Profiles.LisioProfileFactory | LisioProfileFactory}.\
 * To rename a profile is very easy, choose a new revelant name and cahnge old name in {@link LisioProfileNames | LisioProfileNames}.\
 * Next go to {@link Src/Profiles.LisioProfileFactory | LisioProfileFactory}
 *
 * ## How to delete a profile ? Part 1
 *
 * To delete a profile is very easy, delete his name from {@link LisioProfileNames | LisioProfileNames}.\
 * Next go to {@link Src/Profiles.LisioProfileFactory | LisioProfileFactory}
 *
 * ## Documentation
 *
 * Class representing a profile.\
 * It aims to centralize all the logic of profiles.\
 * To execute his responsability this class does :
 *  * Saves name and used parameters
 *  * Formats profile for message exchange
 */
class LisioProfile {
    /**
     * Private attribute to store profile name
     * @source
     */
    _name;
    /**
     * Private attribute to store parameters used by profile
     * @source
     */
    _parameters = new Map();
    /**
     * Getter for attribute {@link _name | _name}
     * @returns Returns _name attribute
     * @source
     */
    get name() {
        return this._name;
    }
    /**
     * Getter for attribute {@link _parameters | _parameters}
     * @returns Returns _parameters attribute
     * @source
     */
    get parameters() {
        return this._parameters;
    }
    /**
     * Public method to parse profile
     * @returns Returns a parsed profile
     * @source
     */
    formatForSaving() {
        const parameters = [];
        for (const parameter of this._parameters.values()) {
            parameters.push(parameter.formatForSaving());
        }
        return {
            name: this._name,
            parameters: parameters,
        };
    }
    /**
     * Public method to test if one of parameters are in profile
     * @param {LisioParameterNames[]} parameterNames - List of parameter to check
     * @returns Returns boolean
     * @source
     */
    checkIfParametersAreInProfile(parameterNames) {
        let end = false;
        if (parameterNames.length > 0) {
            let index = 0;
            let parameterName = parameterNames[index];
            while (index < parameterNames.length && !end) {
                end = this._parameters.has(parameterName);
                index++;
                parameterName = parameterNames[index];
            }
        }
        return end;
    }
    /**
     * Constructor of class {@link LisioProfile | LisioProfile}
     * @param {LisioProfileNames} name - Profile name
     * @param {Map<LisioParameterNames, LisioParameter>} parameters - Parameters used by profile
     * @source
     */
    constructor(name, parameters) {
        this._name = name;
        this._parameters = parameters;
    }
}
export { LisioProfile, LisioProfileNames };
